// Shared modal — records a partial (or full) receipt / payment against a
// pending receivable / payable accounting entry. Used from:
//   • Accounting page (₹ Pay action on each row)
//   • Documents page (Mark Amount Received on each sales invoice)
//
// Prefills the amount to the outstanding balance so "Pay full" is one click.
// Auto-clears the parent when balance hits zero.
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Modal, Field, Input, Select, Btn } from "./kr";
import { DatePicker } from "./DatePicker";
import { MoneyInput } from "./MoneyInput";

// Fixed source options the operator can pick from. "Others" reveals a manual
// text field so unusual sources (e.g. a specific bank) still get captured.
const SOURCE_OPTIONS = ["Online Account", "Cash", "Cheque", "UPI", "Others"];

export default function PaymentModal({ entry, onClose, onSaved }) {
  const noun = entry.entry_type === "receivable" ? "Received" : "Paid";
  const total = Number(entry.amount) || 0;
  const [history, setHistory] = useState({ payments: [], paid_total: 0, balance_due: total });
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [sourceKind, setSourceKind] = useState("Online Account");
  const [sourceOther, setSourceOther] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/accounting/${entry.id}/payments`).then(r => {
      setHistory(r.data);
      setAmount(String(r.data.balance_due || 0));
    }).catch(() => setAmount(String(total)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
    if (amt > (history.balance_due || 0) + 0.005) {
      toast.error(`Amount exceeds outstanding balance (₹${history.balance_due})`);
      return;
    }
    const source = sourceKind === "Others"
      ? (sourceOther.trim() || "Others")
      : sourceKind;
    setBusy(true);
    try {
      const r = await api.post(`/accounting/${entry.id}/pay`,
        { amount: amt, date, source, notes: notes.trim() });
      const bal = r.data?.balance_due ?? 0;
      toast.success(bal <= 0.005
        ? `${noun} in full · ₹${amt.toLocaleString("en-IN")}`
        : `${noun} ₹${amt.toLocaleString("en-IN")} · balance ₹${bal.toLocaleString("en-IN")}`);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const title = `Partial ${noun.toLowerCase()} on ${date} — ${entry.party || ""}`;

  return (
    <Modal open onClose={onClose} title={title} size="md">
      <form onSubmit={submit} className="space-y-4" data-testid="payment-form">
        {/* Totals summary */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="border border-zinc-200 rounded p-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total</div>
            <div className="font-mono font-bold">₹{total.toLocaleString("en-IN")}</div>
          </div>
          <div className="border border-emerald-200 bg-emerald-50/50 rounded p-2">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Already {noun.toLowerCase()}</div>
            <div className="font-mono font-bold text-emerald-800"
                 data-testid="pay-modal-paid">₹{(history.paid_total || 0).toLocaleString("en-IN")}</div>
          </div>
          <div className="border border-orange-300 bg-orange-50/70 rounded p-2">
            <div className="text-[10px] uppercase tracking-widest text-orange-700">Balance</div>
            <div className="font-mono font-bold text-orange-800"
                 data-testid="pay-modal-balance">₹{(history.balance_due || 0).toLocaleString("en-IN")}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={`Amount ${noun.toLowerCase()}`}>
            <MoneyInput value={amount} onChange={e => setAmount(e.target.value)} min={0}
                        required data-testid="pay-amount"
                        className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono" />
          </Field>
          <Field label="Date">
            <DatePicker value={date} onChange={e => setDate(e.target.value)} required data-testid="pay-date" />
          </Field>
          <Field label={`How ${noun.toLowerCase()}?`}>
            <Select value={sourceKind} onChange={e => setSourceKind(e.target.value)}
                    data-testid="pay-source-kind">
              {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label={sourceKind === "Others" ? "Specify source" : "Reference (optional)"}>
            {sourceKind === "Others" ? (
              <Input value={sourceOther} onChange={e => setSourceOther(e.target.value)}
                     placeholder="e.g. HDFC NEFT" data-testid="pay-source-other" />
            ) : (
              <Input value={notes} onChange={e => setNotes(e.target.value)}
                     placeholder="Cheque no, UTR, …" data-testid="pay-notes" />
            )}
          </Field>
          {sourceKind === "Others" && (
            <Field label="Notes (optional)" full>
              <Input value={notes} onChange={e => setNotes(e.target.value)}
                     placeholder="Cheque no, UTR, …" data-testid="pay-notes-others" />
            </Field>
          )}
        </div>

        {/* History */}
        {history.payments?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Previous payments</div>
            <div className="border border-zinc-200 rounded divide-y divide-zinc-100 max-h-32 overflow-y-auto"
                 data-testid="pay-history">
              {history.payments.map(p => (
                <div key={p.id} className="flex justify-between px-2 py-1 text-xs font-mono">
                  <span className="text-zinc-500">{p.date}</span>
                  <span className="text-zinc-700">{p.source || "—"}</span>
                  <span className="font-bold">₹{Number(p.amount).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="pay-submit">
            {busy ? "Saving…" : `Record ${noun}`}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
